import {
  holdThemeColor,
  releaseThemeColor,
} from "@/components/admin/theme-color";
import { EASE_OUT, SPRING_EASING } from "@/lib/desk-motion";

/**
 * Launch splash hand-off (Revolut-style).
 *
 * index.html paints a static navy splash (#avante-splash, outside #root) before
 * any JS. The app keeps it up while something "holds" it (the auth check, the
 * login page's init query, the identity load in the layouts), then removes it:
 * on a cold mobile open with a known user the logo gives way to a big
 * "Hi, {name}" first. No timer decides when the app is ready; a safety timeout
 * only guarantees the splash can never trap the user.
 *
 * Without the #avante-splash element (tests, a second mount) everything here is
 * inert.
 */

export const SPLASH_ELEMENT_ID = "avante-splash";
export const SPLASH_COLOR = "#0b2545";
/** Canvas hand-off duration, read by the html rule in index.html. */
export const SPLASH_OUT_VAR = "--avante-splash-out";
export const GREETED_STORAGE_KEY = "avante.splash.greeted";
/** Never keep the splash longer than this, whatever is still loading. */
export const SPLASH_SAFETY_TIMEOUT_MS = 9000;
/** Holds must stay released this long (a redirect re-holds within it). */
export const SPLASH_SETTLE_MS = 90;
/**
 * Lower bound on the logo, since navigation start (the static splash is
 * painted with the first frame, so this is ~ its on-screen time): long enough
 * to be seen, short enough not to waste time. Never decides readiness: a
 * slower load keeps the splash up for as long as it takes.
 */
export const SPLASH_MIN_VISIBLE_MS = 1000;
/** Same lower bound under reduced motion (no entrance to wait for). */
export const SPLASH_MIN_VISIBLE_REDUCED_MS = 650;
/** The logo's CSS entrance (avante-splash-in in index.html). */
export const SPLASH_LOGO_IN_MS = 420;
/** The logo stays fully in at least this long after its entrance. */
export const SPLASH_LOGO_HOLD_MS = 400;
/**
 * The floating mobile chrome animated in by revealChrome(). It (and the
 * toasts) is hidden while the splash is in the DOM (src/styles/splash.css)
 * and enters once the splash is removed, after the app has been revealed.
 */
export const CHROME_SELECTOR =
  "[data-mobile-tabbar], .mobile-chrome-root > header, .scroll-edge";

/**
 * When the logo may start leaving (ms since navigation start): the minimum,
 * and when the first paint time is known, never before its entrance has played
 * and held a moment (a slow HTML response paints the splash late).
 */
export function splashMinVisibleUntil({
  reducedMotion,
  paintedAt,
}: {
  reducedMotion: boolean;
  paintedAt?: number | null;
}): number {
  if (reducedMotion) return SPLASH_MIN_VISIBLE_REDUCED_MS;
  const painted =
    typeof paintedAt === "number" && Number.isFinite(paintedAt)
      ? paintedAt + SPLASH_LOGO_IN_MS + SPLASH_LOGO_HOLD_MS
      : 0;
  return Math.max(SPLASH_MIN_VISIBLE_MS, painted);
}

const MOBILE_QUERY = "(max-width: 767px)";
const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";

export type SplashIdentity = {
  firstName?: unknown;
  fullName?: unknown;
} | null;

/** First name to greet: identity.firstName, else fullName's first word. */
export function resolveGreetingName(identity?: SplashIdentity): string | null {
  if (!identity) return null;
  const first =
    typeof identity.firstName === "string" ? identity.firstName.trim() : "";
  if (first) return first;
  const full =
    typeof identity.fullName === "string" ? identity.fullName.trim() : "";
  return full ? full.split(/\s+/)[0] : null;
}

/** Greet only a signed-in user (a greeting is set), on mobile, once a session. */
export function shouldGreet({
  greeting,
  isMobile,
  alreadyGreeted,
}: {
  greeting: string | null;
  isMobile: boolean;
  alreadyGreeted: boolean;
}): boolean {
  return Boolean(greeting?.trim()) && isMobile && !alreadyGreeted;
}

export type HandoffTimeline = {
  /** The greeting starts rising in, as the logo dissolves (ms). */
  greetingInAt: number;
  /** The greeting is fully in by then and starts leaving (ms). */
  greetingOutAt: number;
  greetingOutMs: number;
  /** The navy layer starts fading out, revealing the app (ms). */
  layerOutAt: number;
  layerOutMs: number;
  /** The splash is removed from the DOM (ms); the mobile chrome enters. */
  total: number;
  /** The tab bar / top row entrance, from `total`. */
  chromeInMs: number;
};

/** Timings of the hand-off, from the moment the app is ready. */
export function handoffTimeline({
  greet,
  reducedMotion,
}: {
  greet: boolean;
  reducedMotion: boolean;
}): HandoffTimeline {
  if (reducedMotion) {
    // Plain crossfades, opacity only, a little shorter.
    return greet
      ? {
          greetingInAt: 120,
          greetingOutAt: 820,
          greetingOutMs: 240,
          layerOutAt: 860,
          layerOutMs: 260,
          total: 1120,
          chromeInMs: 200,
        }
      : {
          greetingInAt: 0,
          greetingOutAt: 0,
          greetingOutMs: 0,
          layerOutAt: 0,
          layerOutMs: 240,
          total: 240,
          chromeInMs: 200,
        };
  }
  // The greeting is on screen ~1.2 s from its first word to its last frame:
  // it rises as the logo dissolves, is in by ~0.67 s, holds, then lifts away
  // while the navy melts.
  return greet
    ? {
        greetingInAt: 40,
        greetingOutAt: 920,
        greetingOutMs: 320,
        layerOutAt: 980,
        layerOutMs: 460,
        total: 1440,
        chromeInMs: 560,
      }
    : {
        greetingInAt: 0,
        greetingOutAt: 0,
        greetingOutMs: 0,
        layerOutAt: 80,
        layerOutMs: 400,
        total: 480,
        chromeInMs: 560,
      };
}

type Phase = "idle" | "waiting" | "leaving" | "done";

export type SplashEnv = {
  element: HTMLElement;
  storage?: Pick<Storage, "getItem" | "setItem"> | null;
  matches: (query: string) => boolean;
  now: () => number;
  setTimeout: (fn: () => void, ms: number) => unknown;
  clearTimeout: (id: unknown) => void;
  /** Called when the splash colour should stop pinning the OS chrome. */
  releaseChrome?: () => void;
  /** First paint (ms since navigation start), when the browser reports it. */
  paintedAt?: () => number | null;
};

export type SplashController = ReturnType<typeof createSplashController>;

export function createSplashController(env: SplashEnv) {
  let phase: Phase = "idle";
  let holds = 0;
  let greeting: string | null = null;
  let settleTimer: unknown = null;
  let reducedMotion = false;
  const timers = new Set<unknown>();

  const later = (fn: () => void, ms: number) => {
    const id = env.setTimeout(() => {
      timers.delete(id);
      fn();
    }, ms);
    timers.add(id);
    return id;
  };

  const readGreeted = () => {
    try {
      return env.storage?.getItem(GREETED_STORAGE_KEY) === "1";
    } catch {
      return false;
    }
  };

  const markGreeted = () => {
    try {
      env.storage?.setItem(GREETED_STORAGE_KEY, "1");
    } catch {
      // Private mode: the greeting may show again, harmless.
    }
  };

  const finish = () => {
    if (phase === "done") return;
    phase = "done";
    timers.forEach((id) => env.clearTimeout(id));
    timers.clear();
    env.releaseChrome?.();
    env.element.ownerDocument.documentElement.style.removeProperty(
      SPLASH_OUT_VAR,
    );
    env.element.remove();
    // Same task as the removal, so the hidden chrome never shows at full
    // opacity for a frame before its entrance starts.
    revealChrome(env.element.ownerDocument, { reducedMotion });
  };

  const leave = (allowGreeting: boolean) => {
    if (phase === "leaving" || phase === "done") return;
    phase = "leaving";
    if (settleTimer != null) env.clearTimeout(settleTimer);
    timers.forEach((id) => env.clearTimeout(id));
    timers.clear();

    const greet =
      allowGreeting &&
      shouldGreet({
        greeting,
        isMobile: env.matches(MOBILE_QUERY),
        alreadyGreeted: readGreeted(),
      });
    reducedMotion = env.matches(REDUCED_MOTION_QUERY);
    const timeline = handoffTimeline({ greet, reducedMotion });
    if (greet) markGreeted();

    // Taps go to the app as soon as it starts showing.
    env.element.style.pointerEvents = "none";
    animateHandoff(env.element, {
      greeting: greet ? greeting : null,
      reducedMotion,
      timeline,
    });
    later(() => {
      env.releaseChrome?.();
      // The html canvas (navy while the splash is up, see index.html) eases
      // back to the app background as the navy layer melts, so no navy band
      // is left in the iOS strip below the layout viewport (WebKit 313800).
      env.element.ownerDocument.documentElement.style.setProperty(
        SPLASH_OUT_VAR,
        `${timeline.layerOutMs}ms`,
      );
      env.element.dataset.leaving = "";
    }, timeline.layerOutAt);
    later(finish, timeline.total);
  };

  const ready = () => {
    settleTimer = null;
    if (phase !== "waiting" || holds > 0) return;
    const until = splashMinVisibleUntil({
      reducedMotion: env.matches(REDUCED_MOTION_QUERY),
      paintedAt: env.paintedAt?.(),
    });
    const wait = until - env.now();
    if (wait > 0) {
      settleTimer = env.setTimeout(ready, wait);
      return;
    }
    leave(true);
  };

  const check = () => {
    if (phase !== "waiting") return;
    if (settleTimer != null) env.clearTimeout(settleTimer);
    settleTimer = holds > 0 ? null : env.setTimeout(ready, SPLASH_SETTLE_MS);
  };

  return {
    /** True until the hand-off starts. */
    isWaiting: () => phase === "idle" || phase === "waiting",
    /** The app has mounted: from now on, no hold means ready. */
    start() {
      if (phase !== "idle") return;
      phase = "waiting";
      later(() => leave(false), SPLASH_SAFETY_TIMEOUT_MS);
      check();
    },
    /** Keep the splash up; call the returned function to let go. */
    hold(): () => void {
      if (phase === "leaving" || phase === "done") return () => {};
      holds += 1;
      check();
      let released = false;
      return () => {
        if (released) return;
        released = true;
        holds -= 1;
        check();
      };
    },
    /** The localized "Hi, {name}" for the signed-in user (null: no greeting). */
    setGreeting(text: string | null) {
      greeting = text;
    },
    /** Test/debug: skip straight to the end. */
    finish,
  };
}

const supportsLinearEasing = () =>
  typeof CSS !== "undefined" &&
  typeof CSS.supports === "function" &&
  CSS.supports("transition-timing-function", "linear(0, 1)");

function play(
  element: Element | null,
  keyframes: Keyframe[],
  options: KeyframeAnimationOptions,
) {
  if (!element || typeof element.animate !== "function") return;
  element.animate(keyframes, { fill: "both", ...options });
}

function buildGreeting(text: string): HTMLElement {
  const node = document.createElement("p");
  node.className = "avante-splash-greeting";
  text
    .trim()
    .split(/\s+/)
    .forEach((word, index) => {
      if (index > 0) node.append(" ");
      const span = document.createElement("span");
      span.className = "avante-splash-word";
      span.textContent = word;
      node.append(span);
    });
  return node;
}

function animateHandoff(
  element: HTMLElement,
  {
    greeting,
    reducedMotion,
    timeline,
  }: {
    greeting: string | null;
    reducedMotion: boolean;
    timeline: HandoffTimeline;
  },
) {
  const brand = element.querySelector(".avante-splash-brand");
  const app = document.querySelector("[data-splash-reveal]");
  const spring = supportsLinearEasing() ? SPRING_EASING : EASE_OUT;
  const greetingNode = greeting ? buildGreeting(greeting) : null;
  if (greetingNode) element.append(greetingNode);

  if (reducedMotion) {
    play(brand, [{ opacity: 1 }, { opacity: 0 }], {
      duration: 200,
      easing: "ease-out",
    });
    play(greetingNode, [{ opacity: 0 }, { opacity: 1 }], {
      duration: 220,
      delay: timeline.greetingInAt,
      easing: "ease-out",
    });
    play(greetingNode, [{ opacity: 1 }, { opacity: 0 }], {
      duration: timeline.greetingOutMs,
      delay: timeline.greetingOutAt,
      easing: "ease-in-out",
      // Not "both": a backwards fill would pin it at 1 over the fade-in.
      fill: "forwards",
    });
    play(element, [{ opacity: 1 }, { opacity: 0 }], {
      duration: timeline.layerOutMs,
      delay: timeline.layerOutAt,
      easing: "ease-in-out",
    });
    return;
  }

  // The logo dissolves backwards...
  play(
    brand,
    greetingNode
      ? [
          { opacity: 1, transform: "none", filter: "blur(0)" },
          {
            opacity: 0,
            transform: "translateY(-10px) scale(0.9)",
            filter: "blur(10px)",
          },
        ]
      : [
          { opacity: 1, transform: "none" },
          { opacity: 0, transform: "scale(1.08)" },
        ],
    { duration: greetingNode ? 360 : 300, easing: EASE_OUT },
  );

  // ...the greeting rises into focus word by word...
  greetingNode
    ?.querySelectorAll(".avante-splash-word")
    .forEach((word, index) => {
      play(
        word,
        [
          {
            opacity: 0,
            transform: "translateY(20px) scale(0.94)",
            filter: "blur(12px)",
          },
          { opacity: 1, transform: "none", filter: "blur(0)" },
        ],
        {
          duration: 560,
          delay: timeline.greetingInAt + index * 70,
          easing: spring,
        },
      );
    });
  play(
    greetingNode,
    [
      { opacity: 1, transform: "none", filter: "blur(0)" },
      {
        opacity: 0,
        transform: "translateY(-18px) scale(1.06)",
        filter: "blur(12px)",
      },
    ],
    {
      duration: timeline.greetingOutMs,
      delay: timeline.greetingOutAt,
      easing: EASE_OUT,
    },
  );

  // ...and the navy layer melts away over the app, which settles into place.
  play(element, [{ opacity: 1 }, { opacity: 0 }], {
    duration: timeline.layerOutMs,
    delay: timeline.layerOutAt,
    easing: "cubic-bezier(0.4, 0, 0.2, 1)",
  });
  play(
    app,
    [
      { transform: "scale(0.965)", filter: "blur(6px)" },
      { transform: "none", filter: "blur(0)" },
    ],
    {
      duration: 640,
      delay: timeline.layerOutAt,
      easing: spring,
      fill: "backwards",
    },
  );
}

/**
 * The floating mobile chrome enters once the splash is gone: a soft fade and
 * a short spring up (the top row drops in), opacity only under reduced
 * motion. Nothing is ever left hidden: the CSS that hides the chrome only
 * matches while #avante-splash is in the DOM, and these are plain transient
 * animations (no fill) that end on the element's own style.
 */
export function revealChrome(
  doc: Document,
  { reducedMotion }: { reducedMotion: boolean },
) {
  const spring = supportsLinearEasing() ? SPRING_EASING : EASE_OUT;
  const { chromeInMs } = handoffTimeline({ greet: false, reducedMotion });
  doc.querySelectorAll(CHROME_SELECTOR).forEach((node) => {
    if (typeof node.animate !== "function") return;
    if (reducedMotion) {
      node.animate([{ opacity: 0 }, { opacity: 1 }], {
        duration: chromeInMs,
        easing: "ease-out",
      });
      return;
    }
    const fromBottom = node.matches("[data-mobile-tabbar]");
    const isEdge = node.matches(".scroll-edge");
    node.animate(
      [
        {
          transform: isEdge
            ? "none"
            : fromBottom
              ? "translateY(18px) scale(0.97)"
              : "translateY(-8px)",
        },
        { transform: "none" },
      ],
      { duration: chromeInMs, easing: spring },
    );
    // Opacity settles before the spring does, so it never looks see-through.
    node.animate([{ opacity: 0 }, { opacity: 1 }], {
      duration: Math.round(chromeInMs * 0.55),
      easing: "ease-out",
    });
  });
}

const createBrowserController = (): SplashController | null => {
  if (typeof document === "undefined") return null;
  const element = document.getElementById(SPLASH_ELEMENT_ID);
  if (!element) return null;
  holdThemeColor(SPLASH_COLOR);
  let storage: Storage | null = null;
  try {
    storage = window.sessionStorage;
  } catch {
    storage = null;
  }
  return createSplashController({
    element,
    storage,
    matches: (query) =>
      typeof window.matchMedia === "function" &&
      window.matchMedia(query).matches,
    now: () => performance.now(),
    setTimeout: (fn, ms) => window.setTimeout(fn, ms),
    clearTimeout: (id) => window.clearTimeout(id as number),
    releaseChrome: releaseThemeColor,
    paintedAt: () => {
      try {
        const paint = performance.getEntriesByType?.("paint")?.[0];
        return paint ? paint.startTime : null;
      } catch {
        return null;
      }
    },
  });
};

/** The page's splash, or null when there is none (tests, HMR after hand-off). */
export const launchSplash = createBrowserController();
